

export type Language = 'en' | 'ja';

export const translations = {
  en: {
    // Sidebar Header
    appName: "Mind Mandala",
    
    // Modes & Toggles
    darkMode: "Dark Mode",
    lightMode: "Light Mode",
    language: "Language",
    showEmoji: "Show Emoji",
    mandalaMode: "Mandala Mode",
    darkModeTooltip: "Toggle between Dark and Light mode",
    languageTooltip: "Switch language between English and Japanese",
    emojiTooltip: "Toggle emoji visibility on nodes",
    mandalaModeTooltip: "Toggle between tree view and radial mandala view",
    
    // File Operations
    fileOperations: "File Operations",
    createNew: "Create New",
    createNewTooltip: "Create a new blank mind map",
    openMarkdown: "Import Markdown",
    openMarkdownTooltip: "Import a markdown file from your computer",
    saveMindMap: "Export Markdown",
    saveTooltip: "Download current map as a markdown file",
    unsavedChanges: "You have unsaved changes. Are you sure you want to discard them?",
    
    // Actions
    expandAll: "Expand All",
    expandAllTooltip: "Expand all collapsed nodes",
    collapse: "Collapse",
    collapseTooltip: "Collapse the lowest expanded nodes",
    rearrange: "Rearrange",
    rearrangeTooltip: "Re-organize layout to remove overlaps",
    undo: "Undo",
    undoTooltip: "Undo the last action",
    reset: "Reset",
    resetTooltip: "Reset layout and revert to original file state",
    replay: "Replay Ideation",
    replayTooltip: "Replay the development of the mind map",
    replaying: "Replaying...",
    
    // Theme
    theme: "Theme",
    dynamicTip: "Colors change based on keywords in the text (e.g., 'error', 'success', 'sql').",
    
    // AI Section
    aiAssistant: "AI Assistant",
    selectNodeWarning: "Select a node in the map to unlock AI features.",
    selected: "Selected",
    expandIdeas: "Expand Ideas",
    explainConcept: "Explain Concept",
    thinking: "Thinking...",
    
    // Footer
    doubleClickTip: "Double click nodes to edit text",
    updated: "Updated",
    tabTip: "Tab to add node • Double click background to add floating node",
    
    // Context Menu
    editText: "Edit text",
    insertSubTopic: "Insert sub topic",
    editDescription: "Add/Edit Description",
    delete: "Delete",
    
    // Dialogs
    deleteNodeTitle: "Delete Node?",
    deleteNodeBody: (name: string) => `"${name}" has sub-topics. How do you want to handle them?`,
    deleteSubtree: "Delete entire subtree",
    deleteSubtreeDesc: "Remove this node and all its children",
    keepChildren: "Keep children",
    keepChildrenDesc: "Promote children to the parent node",
    floatChildren: "Float children",
    floatChildrenDesc: "Detach children and make them floating",
    
    descriptionTitle: "Edit Description",
    descriptionPlaceholder: "Enter a brief description...",
    save: "Save",
    cancel: "Cancel",
    
    // Node Defaults
    newTopic: "New Topic",
    rootName: "Mind Map"
  },
  ja: {
    // Sidebar Header
    appName: "Mind Mandala",
    
    // Modes & Toggles
    darkMode: "ダークモード",
    lightMode: "ライトモード",
    language: "言語",
    showEmoji: "絵文字を表示",
    mandalaMode: "曼荼羅モード",
    darkModeTooltip: "ダークモードとライトモードを切り替え",
    languageTooltip: "英語と日本語を切り替え",
    emojiTooltip: "ノードの絵文字表示を切り替え",
    mandalaModeTooltip: "ツリー表示と円形の曼荼羅表示を切り替えます",
    
    // File Operations
    fileOperations: "ファイル操作",
    createNew: "新規作成",
    createNewTooltip: "新しい空のマインドマップを作成",
    openMarkdown: "Markdownをインポート",
    openMarkdownTooltip: "コンピュータからMarkdownファイルをインポート",
    saveMindMap: "Markdownをエクスポート",
    saveTooltip: "現在のマップをMarkdownファイルとしてダウンロード",
    unsavedChanges: "保存されていない変更があります。破棄してもよろしいですか？",
    
    // Actions
    expandAll: "全て展開",
    expandAllTooltip: "折りたたまれたノードを全て展開",
    collapse: "折りたたむ",
    collapseTooltip: "最下層のノードを折りたたむ",
    rearrange: "整列",
    rearrangeTooltip: "重なりを解消してレイアウトを整列",
    undo: "元に戻す",
    undoTooltip: "最後の操作を元に戻す",
    reset: "リセット",
    resetTooltip: "レイアウトをリセットして初期状態に戻す",
    replay: "発想過程を再生",
    replayTooltip: "マインドマップの発達過程を再生",
    replaying: "再生中...",
    
    // Theme
    theme: "テーマ",
    dynamicTip: "テキスト内のキーワードに基づいて色が変化します（例：「エラー」「成功」「SQL」）。",
    
    // AI Section
    aiAssistant: "AIアシスタント",
    selectNodeWarning: "ノードを選択してAI機能を使用してください。",
    selected: "選択中",
    expandIdeas: "アイデアを展開",
    explainConcept: "概念を解説",
    thinking: "思考中...",
    
    // Footer
    doubleClickTip: "ダブルクリックでテキスト編集",
    updated: "更新",
    tabTip: "Tabキーでノードを追加 • 背景ダブルクリックで浮動ノードを作成",
    
    // Context Menu
    editText: "テキストを編集",
    insertSubTopic: "サブトピックを追加",
    editDescription: "説明を追加/編集",
    delete: "削除",
    
    // Dialogs
    deleteNodeTitle: "ノードを削除しますか？",
    deleteNodeBody: (name: string) => `"${name}" にはサブトピックがあります。どのように処理しますか？`,
    deleteSubtree: "サブツリーごと削除",
    deleteSubtreeDesc: "このノードと全ての子ノードを削除",
    keepChildren: "子ノードを保持",
    keepChildrenDesc: "子ノードを親ノードに移動",
    floatChildren: "子ノードを浮動化",
    floatChildrenDesc: "子ノードを切り離して浮動ノードにする",
    
    descriptionTitle: "説明を編集",
    descriptionPlaceholder: "短い説明を入力してください...",
    save: "保存",
    cancel: "キャンセル",
    
    // Node Defaults
    newTopic: "新規トピック",
    rootName: "マインドマップ"
  }
};